import * as React from 'react';
import clsx from 'clsx';
import { VideoCameraIcon } from '@heroicons/react/24/solid';
import { Tooltip } from '@/components/ui/Tooltip';
import type { MessageRating } from '@/types/ratings';
import { useToast } from '@/context/ToastContext';
import { FeedbackModal } from '@/components/feedback';
import type { FeedbackVariant } from '@/components/feedback/feedbackOptions';

// Idle Icons
import playIcon from '@/assets/images/svg/play_icon.svg';
import copyIcon from '@/assets/images/svg/copy_icon.svg';
import shareIcon from '@/assets/images/svg/share_icon.svg';
import rateIcon from '@/assets/images/svg/rate_icon.svg';
import badResponseIcon from '@/assets/images/svg/bad_response.svg';
import goodResponseIcon from '@/assets/images/svg/good_response.svg';

// Pressed/Active Icons
import playIconPressed from '@/assets/images/svg/play_icon_pressed.svg';
import copyIconPressed from '@/assets/images/svg/copy_icon_pressed.svg';
import shareIconPressed from '@/assets/images/svg/share_icon_pressed.svg';
import rateIconPressed from '@/assets/images/svg/rate_icon_pressed.svg';
import badResponsePressedIcon from '@/assets/images/svg/bad_response_pressed.svg';
import goodResponsePressedIcon from '@/assets/images/svg/good_response_pressed.svg';

interface MessageActionsProps {
  onPlay: () => void;
  isPlaying: boolean;
  onCopy: () => void;
  onShare: () => void;
  rating: MessageRating;
  onRate: (type: 'like' | 'dislike' | 'super_like') => void;
  hasAudio: boolean;
  isAudioEnabled?: boolean;
  isAudioGenerated?: boolean;
  onFeedbackSubmit: (
    variant: FeedbackVariant,
    selectedOptions: string[],
    additionalText: string
  ) => void;
  isFeedbackLoading?: boolean;
  checkIsFirstRatingForEcho: () => boolean;
  // Video
  hasVideoSupport?: boolean;
  videoStatus?: string | null;
  onVideoClick?: () => void;
  isVideoGenerating?: boolean;
  messageLength?: number;
}

/**
 * Action buttons for AI messages including utility actions (play, copy, share) and rating actions.
 * Displays in two separate containers:
 * - Container 1: Utility actions (play audio, copy text, share message)
 * - Container 2: Rating actions (super like, dislike, like)
 *
 * @param {MessageActionsProps} props - Component props
 * @param {() => void} props.onPlay - Callback to play or pause audio
 * @param {boolean} props.isPlaying - Whether audio is currently playing
 * @param {() => void} props.onCopy - Callback to copy message text to clipboard
 * @param {() => void} props.onShare - Callback to share message
 * @param {MessageRating} props.rating - Current rating state (is_liked, is_disliked, is_super_liked)
 * @param {(type: 'like' | 'dislike' | 'super_like') => void} props.onRate - Callback to submit rating
 * @param {boolean} props.hasAudio - Whether message has audio available
 * @param {boolean} [props.isAudioGenerated=false] - Whether audio was successfully generated
 */
const MAX_VIDEO_CHARS = 500;

function resolveActiveVariant(rating: MessageRating): FeedbackVariant | null {
  if (rating.is_super_liked) return 'star';
  if (rating.is_disliked) return 'dislike';
  if (rating.is_liked) return 'like';
  return null;
}

function rateTypeToFeedbackVariant(type: 'like' | 'dislike' | 'super_like'): FeedbackVariant {
  if (type === 'super_like') return 'star';
  if (type === 'dislike') return 'dislike';
  return 'like';
}

function getVideoTooltip(
  tooLong: boolean,
  isGenerating: boolean,
  isDone: boolean,
  isFailed: boolean
): string {
  if (tooLong) return `Message too long for video (max ${MAX_VIDEO_CHARS} chars)`;
  if (isGenerating) return 'Generating video...';
  if (isDone) return 'Play video';
  if (isFailed) return 'Retry video';
  return 'Generate video';
}

interface AudioActionProps {
  hasAudio: boolean;
  isAudioGenerated: boolean;
  isPlaying: boolean;
  onPlay: () => void;
}

const AudioAction: React.FC<AudioActionProps> = ({
  hasAudio,
  isAudioGenerated,
  isPlaying,
  onPlay,
}) => {
  const { addToast } = useToast();
  const isAudioReady = hasAudio && isAudioGenerated;

  if (!hasAudio) return null;

  const handlePlayClick = () => {
    if (!isAudioReady) {
      addToast({
        title: 'Audio Unavailable',
        subtitle: 'Audio generation was skipped for this message, or it contained no audio.',
        type: 'info',
      });
      return;
    }
    onPlay();
  };

  return (
    <Tooltip
      content={!isAudioReady ? 'Audio loading…' : isPlaying ? 'Pause' : 'Play'}
      position='bottom'
    >
      <button
        onClick={handlePlayClick}
        className={clsx(
          'flex size-5 shrink-0 items-center justify-center',
          isAudioReady ? 'cursor-pointer' : 'cursor-not-allowed'
        )}
        aria-label={!isAudioReady ? 'Audio loading' : isPlaying ? 'Pause audio' : 'Play audio'}
        disabled={!isAudioReady}
      >
        <img
          src={isPlaying ? playIconPressed : playIcon}
          alt={isPlaying ? 'Playing' : 'Play'}
          className={clsx(
            'size-5 object-contain transition-[filter,opacity] duration-150',
            isAudioReady ? 'opacity-100' : 'opacity-50 grayscale'
          )}
        />
      </button>
    </Tooltip>
  );
};

export const MessageActions: React.FC<MessageActionsProps> = ({
  onPlay,
  isPlaying,
  onCopy,
  onShare,
  rating,
  onRate,
  hasAudio,
  isAudioGenerated = false,
  onFeedbackSubmit,
  checkIsFirstRatingForEcho,
  hasVideoSupport = false,
  videoStatus,
  onVideoClick,
  isVideoGenerating = false,
  messageLength = 0,
}) => {
  const [activeAction, setActiveAction] = React.useState<string | null>(null);
  const [feedbackModalOpen, setFeedbackModalOpen] = React.useState(false);
  const [autoOpenVariant, setAutoOpenVariant] = React.useState<FeedbackVariant | null>(null);
  const transientTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(
    () => () => {
      if (transientTimeoutRef.current) clearTimeout(transientTimeoutRef.current);
    },
    []
  );

  const activeVariant = resolveActiveVariant(rating);

  /**
   * Triggers an action and shows visual feedback for 2 seconds.
   * Used for copy and share actions to display "Copied!" or "Shared!" states.
   *
   * @param {string} action - Action identifier for visual state
   * @param {() => void} callback - Function to execute immediately
   */
  const handleTransientAction = (action: string, callback: () => void) => {
    setActiveAction(action);
    callback();
    if (transientTimeoutRef.current) clearTimeout(transientTimeoutRef.current);
    transientTimeoutRef.current = setTimeout(() => {
      setActiveAction(null);
      transientTimeoutRef.current = null;
    }, 2000);
  };

  const handleRateClick = (type: 'like' | 'dislike' | 'super_like') => {
    const isFirstEver = checkIsFirstRatingForEcho();
    onRate(type);
    if (isFirstEver) {
      setAutoOpenVariant(rateTypeToFeedbackVariant(type));
      setFeedbackModalOpen(true);
    }
  };

  const modalVariant = activeVariant ?? autoOpenVariant;

  const handleFeedbackSubmit = (selectedOptions: string[], additionalText: string) => {
    if (modalVariant) {
      onFeedbackSubmit(modalVariant, selectedOptions, additionalText);
    }
    setFeedbackModalOpen(false);
    setAutoOpenVariant(null);
  };

  const handleFeedbackClose = () => {
    setFeedbackModalOpen(false);
    setAutoOpenVariant(null);
  };

  return (
    <div className='ml-2 mt-4 flex flex-wrap items-center gap-3 xl:ml-6 xl:gap-[26px]'>
      {/* Fixed play slot: readiness changes color only, never row geometry. */}
      {hasAudio && (
        <div className='flex size-5 shrink-0 items-center justify-center'>
          <AudioAction
            hasAudio={hasAudio}
            isAudioGenerated={isAudioGenerated}
            isPlaying={isPlaying}
            onPlay={onPlay}
          />
        </div>
      )}

      {/* Video remains independent from audio readiness. */}
      <div className='flex items-center gap-4'>
        {hasVideoSupport &&
          onVideoClick &&
          (() => {
            const tooLong = messageLength > MAX_VIDEO_CHARS;
            const isDone = videoStatus === 'done';
            const isFailed = videoStatus === 'failed';
            const isDisabled = tooLong || isVideoGenerating;
            const tooltip = getVideoTooltip(tooLong, isVideoGenerating, isDone, isFailed);

            return (
              <Tooltip content={tooltip} position='bottom'>
                <button
                  onClick={onVideoClick}
                  disabled={isDisabled}
                  className={clsx(
                    'transition-transform hover:scale-110 active:scale-95',
                    isDone
                      ? 'text-accent-300 opacity-100'
                      : 'text-accent-300/70 opacity-80 hover:opacity-100',
                    isDisabled && 'cursor-not-allowed opacity-40'
                  )}
                  aria-label={tooltip}
                >
                  {isVideoGenerating ? (
                    <span className='block size-5 animate-spin rounded-full border-2 border-accent-300 border-t-transparent' />
                  ) : (
                    <VideoCameraIcon className='size-5' />
                  )}
                </button>
              </Tooltip>
            );
          })()}
      </div>

      {/* Container 2: Copy + Share */}
      <div className='flex items-center gap-4'>
        <Tooltip content={activeAction === 'copy' ? 'Copied!' : 'Copy'} position='bottom'>
          <button
            onClick={() => handleTransientAction('copy', onCopy)}
            className={clsx(
              'transition-transform hover:scale-110 active:scale-95',
              activeAction === 'copy' ? 'opacity-100' : 'opacity-80 hover:opacity-100'
            )}
            aria-label='Copy message'
          >
            <img
              src={activeAction === 'copy' ? copyIconPressed : copyIcon}
              alt='Copy'
              className='size-5'
            />
          </button>
        </Tooltip>

        <Tooltip content='Share' position='bottom'>
          <button
            onClick={() => handleTransientAction('share', onShare)}
            className={clsx(
              'transition-transform hover:scale-110 active:scale-95',
              activeAction === 'share' ? 'opacity-100' : 'opacity-80 hover:opacity-100'
            )}
            aria-label='Share message'
          >
            <img
              src={activeAction === 'share' ? shareIconPressed : shareIcon}
              alt='Share'
              className='size-5'
            />
          </button>
        </Tooltip>
      </div>

      {/* Container 2: Rating Actions */}
      <div className='flex items-center gap-4'>
        {/* Rate (Super Like) */}
        <Tooltip content={rating.is_super_liked ? 'Super Liked' : 'Super Like'} position='bottom'>
          <button
            onClick={() => handleRateClick('super_like')}
            className={clsx(
              'transition-transform hover:scale-110 active:scale-95',
              rating.is_super_liked ? 'opacity-100' : 'opacity-80 hover:opacity-100'
            )}
            aria-label='Super Like'
          >
            <img
              src={rating.is_super_liked ? rateIconPressed : rateIcon}
              alt='Super Like'
              className='size-5'
            />
          </button>
        </Tooltip>

        {/* Bad Response (Dislike) */}
        <Tooltip content='Bad response' position='bottom'>
          <button
            onClick={() => handleRateClick('dislike')}
            className={clsx(
              'transition-transform hover:scale-110 active:scale-95',
              rating.is_disliked ? 'opacity-100' : 'opacity-80 hover:opacity-100'
            )}
            aria-label='Bad response'
          >
            <img
              src={rating.is_disliked ? badResponsePressedIcon : badResponseIcon}
              alt='Bad response'
              className='size-5'
            />
          </button>
        </Tooltip>

        {/* Good Response (Like) */}
        <Tooltip content='Good response' position='bottom'>
          <button
            onClick={() => handleRateClick('like')}
            className={clsx(
              'transition-transform hover:scale-110 active:scale-95',
              rating.is_liked ? 'opacity-100' : 'opacity-80 hover:opacity-100'
            )}
            aria-label='Good response'
          >
            <img
              src={rating.is_liked ? goodResponsePressedIcon : goodResponseIcon}
              alt='Good response'
              className='size-5'
            />
          </button>
        </Tooltip>
      </div>

      {activeVariant && !feedbackModalOpen && (
        <button
          onClick={() => setFeedbackModalOpen(true)}
          className='rounded-full bg-brand-800 px-3 py-1 font-sans text-[10px] font-semibold text-white transition-opacity hover:opacity-80 xl:px-4 xl:py-1.5 xl:text-12'
        >
          Tell us more
        </button>
      )}

      {modalVariant && (
        <FeedbackModal
          isOpen={feedbackModalOpen}
          onClose={handleFeedbackClose}
          variant={modalVariant}
          onSubmit={handleFeedbackSubmit}
        />
      )}
    </div>
  );
};
